import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Heading,
  Divider,
  IconButton,
  Loader,
  Toggle,
  Text,
} from '@vibe/core';
import { Close } from '@vibe/icons';
import FieldMappingRow, { matchesExpectedType } from './FieldMappingRow';
import {
  getBoardConfig,
  updateBoardConfig,
  getBoardColumns,
  getSubitemsBoardColumns,
  type BoardConfig,
  type FieldMapping,
  type MondayColumn,
  type ItemLevel,
  type ColorSchema,
} from '../../api/board-config.api';
import { getAppSettings } from '../../api/settings.api';
import { useWorkloadStore } from '../../stores/workloadStore';
import {
  getWorklogCategories,
  type WorklogCategory,
} from '../../api/worklog-category.api';

// ─── Props ────────────────────────────────────────────────────────────────────

interface BoardSettingsProps {
  boardId: number;
  onClose: () => void;
  onSaved?: () => void;
  /** When in multi-board mode, all board options for the board selector */
  multiBoardOptions?: Array<{ id: number; name: string }>;
}

// ─── Dropdown options ─────────────────────────────────────────────────────────

const ITEM_LEVEL_OPTIONS: { value: ItemLevel; label: string }[] = [
  { value: 'main', label: 'Main items only' },
  { value: 'sub', label: 'Sub-items only' },
  { value: 'both', label: 'Both main & sub-items' },
];

const COLOR_SCHEMA_OPTIONS: { value: ColorSchema; label: string }[] = [
  { value: 'picker', label: 'Color Picker column' },
  { value: 'group', label: 'Group color' },
  { value: 'status', label: 'Status color' },
];

const PER_PERSON_OPTIONS: { value: 'default' | 'on' | 'off'; label: string }[] = [
  { value: 'default', label: 'Use account default' },
  { value: 'on', label: 'Enabled' },
  { value: 'off', label: 'Disabled' },
];

// ─── Field mapping definitions ───────────────────────────────────────────────

interface FieldDef {
  key: keyof FieldMapping;
  label: string;
  description: string;
  expectedType: string;
  /** When true, any column type is accepted (no type validation on save) */
  skipTypeValidation?: boolean;
}

const FIELD_DEFINITIONS: FieldDef[] = [
  {
    key: 'scheduledHoursColumnId',
    label: 'Original Estimate',
    description: 'Numbers column for total estimated hours — used as baseline for remaining estimate',
    expectedType: 'Numbers',
  },
  {
    key: 'scheduledHoursPerDayColumnId',
    label: 'Hours Per Day',
    description: 'Numbers column for daily effort allocation — constant rate per working day',
    expectedType: 'Numbers',
  },
  {
    key: 'assigneesColumnId',
    label: 'Assignees',
    description: 'People column for item assignees',
    expectedType: 'People',
  },
  {
    key: 'timelineColumnId',
    label: 'Timeline',
    description: 'Timeline column for date ranges',
    expectedType: 'Timeline',
  },
  {
    key: 'statusColumnId',
    label: 'Status',
    description: 'Status, Text, or Numbers column for task status',
    expectedType: 'Status',
    skipTypeValidation: true,
  },
  {
    key: 'colorColumnId',
    label: 'Color',
    description: 'Color Picker column for item colors',
    expectedType: 'Color Picker',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Board settings panel displayed as a sidebar/modal.
 *
 * Contains:
 * - Field Mapping section
 * - Display settings (item level, color schema, item height)
 * - Workload settings (capacity mode, work hours, exclude completed)
 */
const BoardSettings: React.FC<BoardSettingsProps> = ({ boardId, onClose, onSaved, multiBoardOptions }) => {
  // In multi-board mode, let the user pick which board to configure
  const [activeBoardId, setActiveBoardId] = useState(boardId);
  const isMultiBoard = multiBoardOptions && multiBoardOptions.length > 1;

  const [config, setConfig] = useState<BoardConfig | null>(null);
  const [columns, setColumns] = useState<MondayColumn[]>([]);
  const [subitemColumns, setSubitemColumns] = useState<MondayColumn[]>([]);
  const [worklogCategories, setWorklogCategories] = useState<WorklogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load board configuration and available columns
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [boardConfig, boardColumns, subResult, cats] = await Promise.all([
          getBoardConfig(activeBoardId),
          getBoardColumns(activeBoardId),
          getSubitemsBoardColumns(activeBoardId).catch(() => ({ subitemsBoardId: null, columns: [] })),
          getWorklogCategories(false).catch(() => [] as WorklogCategory[]),
        ]);
        if (!cancelled) {
          setConfig(boardConfig);
          setColumns(boardColumns);
          if (subResult.columns.length > 0) {
            setSubitemColumns(subResult.columns);
          } else {
            setSubitemColumns([]);
          }
          setWorklogCategories(cats);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load settings';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeBoardId]);

  // Save configuration to backend
  const handleSave = useCallback(async () => {
    if (!config) return;
    setIsSaving(true);
    setError(null);

    // Validate column types before saving
    const validateMapping = (mapping: FieldMapping, cols: MondayColumn[], label: string): string[] => {
      const errors: string[] = [];
      for (const field of FIELD_DEFINITIONS) {
        if (field.skipTypeValidation) continue;
        const colId = mapping[field.key];
        if (!colId) continue;
        const col = cols.find((c) => c.id === colId);
        if (col && !matchesExpectedType(col.type, field.expectedType)) {
          errors.push(`${label} "${field.label}" expects ${field.expectedType} but "${col.title}" is ${col.type}`);
        }
      }
      return errors;
    };

    const showMainMapping = config.itemLevel === 'main' || config.itemLevel === 'both';
    const showSubMapping = config.itemLevel === 'sub' || config.itemLevel === 'both';

    const validationErrors: string[] = [];
    if (showMainMapping) {
      validationErrors.push(...validateMapping(config.fieldMapping, columns, 'Main Items'));
    }
    if (showSubMapping) {
      const subCols = subitemColumns.length > 0 ? subitemColumns : columns;
      validationErrors.push(...validateMapping(config.subitemFieldMapping, subCols, 'Sub-items'));
    }

    if (validationErrors.length > 0) {
      setError(`Column type mismatch:\n${validationErrors.join('\n')}`);
      setIsSaving(false);
      return;
    }

    try {
      await updateBoardConfig(activeBoardId, {
        fieldMapping: config.fieldMapping,
        subitemFieldMapping: config.subitemFieldMapping,
        itemLevel: config.itemLevel,
        colorSchema: config.colorSchema,
        perPersonDistribution: config.perPersonDistribution,
        showParentItemName: config.showParentItemName,
        defaultWorklogCategoryId: config.defaultWorklogCategoryId,
      });
      // Resolve effective per-person distribution: board override > account default
      let effectivePerPerson = config.perPersonDistribution;
      if (effectivePerPerson === null) {
        try {
          const appSettings = await getAppSettings();
          effectivePerPerson = appSettings.perPersonDistribution;
        } catch {
          effectivePerPerson = false;
        }
      }
      // Push resolved settings to the workload store so scheduler recomputes immediately
      const { setPerPersonDistribution } = useWorkloadStore.getState();
      setPerPersonDistribution(effectivePerPerson);
      onSaved?.();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save settings';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }, [activeBoardId, config, columns, subitemColumns, onClose, onSaved]);

  // Field mapping change handler
  const handleFieldMappingChange = useCallback(
    (key: keyof FieldMapping, columnId: string | null) => {
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fieldMapping: {
            ...prev.fieldMapping,
            [key]: columnId,
          },
        };
      });
    },
    [],
  );

  // Sub-item field mapping change handler
  const handleSubitemFieldMappingChange = useCallback(
    (key: keyof FieldMapping, columnId: string | null) => {
      setConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          subitemFieldMapping: {
            ...prev.subitemFieldMapping,
            [key]: columnId,
          },
        };
      });
    },
    [],
  );

  const showMainMapping = config?.itemLevel === 'main' || config?.itemLevel === 'both';
  const showSubitemMapping = config?.itemLevel === 'sub' || config?.itemLevel === 'both';

  // Generic config update helper
  const updateConfig = useCallback(
    <K extends keyof BoardConfig>(key: K, value: BoardConfig[K]) => {
      setConfig((prev) => {
        if (!prev) return prev;
        return { ...prev, [key]: value };
      });
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="rp-board-settings">
        <div className="rp-board-settings__header">
          <Heading type={Heading.types.H2}>Board Settings</Heading>
          <IconButton
            icon={Close}
            size={IconButton.sizes.SMALL}
            onClick={onClose}
            ariaLabel="Close settings"
          />
        </div>
        <div className="rp-board-settings__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="rp-board-settings">
        <div className="rp-board-settings__header">
          <Heading type={Heading.types.H2}>Board Settings</Heading>
          <IconButton
            icon={Close}
            size={IconButton.sizes.SMALL}
            onClick={onClose}
            ariaLabel="Close settings"
          />
        </div>
        <div className="rp-board-settings__error">
          <p>{error}</p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="rp-board-settings">
      {/* Header */}
      <div className="rp-board-settings__header">
        <Heading type={Heading.types.H2}>Board Settings</Heading>
        <IconButton
          icon={Close}
          size={IconButton.sizes.SMALL}
          onClick={onClose}
          ariaLabel="Close settings"
        />
      </div>

      <div className="rp-board-settings__body">
        {/* ─── Board Selector (multi-board mode) ─────────────────── */}
        {isMultiBoard && (
          <section className="rp-board-settings__section">
            <div className="rp-board-settings__field">
              <label className="rp-board-settings__label">Configure board</label>
              <Dropdown
                size={Dropdown.sizes.SMALL}
                options={multiBoardOptions.map((b) => ({ value: b.id, label: b.name }))}
                value={multiBoardOptions
                  .map((b) => ({ value: b.id, label: b.name }))
                  .find((opt) => opt.value === activeBoardId)}
                onChange={(option) => {
                  if (option) {
                    const selected = option as { value: number; label: string };
                    setActiveBoardId(selected.value);
                  }
                }}
                insideOverflowContainer
                className="rp-board-settings__dropdown"
              />
            </div>
            <Divider />
          </section>
        )}

        {/* ─── Field Mapping (main items) ──────────────────────────── */}
        {showMainMapping && (
        <section className="rp-board-settings__section">
          <Heading type={Heading.types.H3}>Field Mapping</Heading>
          <p className="rp-board-settings__description">
            Map monday.com columns to resource planning concepts.
          </p>

          <div className="rp-board-settings__field-mappings">
            {FIELD_DEFINITIONS.map((field) => (
              <FieldMappingRow
                key={field.key}
                label={field.label}
                description={field.description}
                expectedType={field.expectedType}
                selectedColumnId={config.fieldMapping[field.key]}
                columns={columns}
                onChange={(columnId) =>
                  handleFieldMappingChange(field.key, columnId)
                }
                skipTypeValidation={field.skipTypeValidation}
              />
            ))}
          </div>
        </section>
        )}

        {/* ─── Sub-item Field Mapping ──────────────────────────────── */}
        {showSubitemMapping && (
          <>
            <Divider />
            <section className="rp-board-settings__section">
              <Heading type={Heading.types.H3}>Sub-item Field Mapping</Heading>
              <p className="rp-board-settings__description">
                Map sub-item columns separately (sub-items live on a different board with their own columns).
              </p>

              <div className="rp-board-settings__field-mappings">
                {FIELD_DEFINITIONS.map((field) => (
                  <FieldMappingRow
                    key={`sub-${field.key}`}
                    label={field.label}
                    description={field.description}
                    expectedType={field.expectedType}
                    selectedColumnId={config.subitemFieldMapping[field.key]}
                    columns={subitemColumns.length > 0 ? subitemColumns : columns}
                    onChange={(columnId) =>
                      handleSubitemFieldMappingChange(field.key, columnId)
                    }
                    skipTypeValidation={field.skipTypeValidation}
                  />
                ))}
              </div>
            </section>
          </>
        )}

        <Divider />

        {/* ─── Display Settings ──────────────────────────────────── */}
        <section className="rp-board-settings__section">
          <Heading type={Heading.types.H3}>Display Settings</Heading>

          <div className="rp-board-settings__fields">
            <div className="rp-board-settings__field">
              <label className="rp-board-settings__label">Item Level</label>
              <Dropdown
                size={Dropdown.sizes.SMALL}
                options={ITEM_LEVEL_OPTIONS}
                value={ITEM_LEVEL_OPTIONS.find(
                  (opt) => opt.value === config.itemLevel,
                )}
                onChange={(option) => {
                  if (option) {
                    const selected = option as {
                      value: ItemLevel;
                      label: string;
                    };
                    updateConfig('itemLevel', selected.value);
                  }
                }}
                insideOverflowContainer
                className="rp-board-settings__dropdown"
              />
            </div>

            <div className="rp-board-settings__field">
              <label className="rp-board-settings__label">Color Schema</label>
              <Dropdown
                size={Dropdown.sizes.SMALL}
                options={COLOR_SCHEMA_OPTIONS}
                value={COLOR_SCHEMA_OPTIONS.find(
                  (opt) => opt.value === config.colorSchema,
                )}
                onChange={(option) => {
                  if (option) {
                    const selected = option as {
                      value: ColorSchema;
                      label: string;
                    };
                    updateConfig('colorSchema', selected.value);
                  }
                }}
                insideOverflowContainer
                className="rp-board-settings__dropdown"
              />
            </div>

            {showSubitemMapping && (
              <div className="rp-board-settings__field">
                <label className="rp-board-settings__label">Show Parent Item Name</label>
                <Toggle
                  isSelected={config.showParentItemName ?? false}
                  onChange={(value) => updateConfig('showParentItemName', value)}
                  size="small"
                />
                <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color)', marginTop: 4 }}>
                  Display parent item name as a badge on subitem events in the scheduler.
                </Text>
              </div>
            )}

          </div>
        </section>

        <Divider />

        {/* ─── Workload Settings ──────────────────────────────────── */}
        <section className="rp-board-settings__section">
          <Heading type={Heading.types.H3}>Workload Settings</Heading>

          <div className="rp-board-settings__fields">
            <div className="rp-board-settings__field">
              <label className="rp-board-settings__label">
                Per-Person Hour Distribution
              </label>
              <Dropdown
                size={Dropdown.sizes.SMALL}
                options={PER_PERSON_OPTIONS}
                value={PER_PERSON_OPTIONS.find(
                  (opt) => opt.value === (config.perPersonDistribution === null ? 'default' : config.perPersonDistribution ? 'on' : 'off'),
                )}
                onChange={(option) => {
                  if (option) {
                    const selected = option as { value: 'default' | 'on' | 'off'; label: string };
                    updateConfig('perPersonDistribution', selected.value === 'default' ? null : selected.value === 'on');
                  }
                }}
                insideOverflowContainer
                className="rp-board-settings__dropdown"
              />
              <p className="rp-board-settings__hint">
                "Use account default" inherits the value from App Settings. When enabled, task hours are divided equally among all assignees.
              </p>
            </div>

          </div>

          {worklogCategories.length > 0 && (
            <div className="rp-board-settings__field">
              <label className="rp-board-settings__label">Default Worklog Category</label>
              <Dropdown
                size={Dropdown.sizes.SMALL}
                options={[
                  { value: '', label: 'Use system default' },
                  ...worklogCategories.map((c) => ({ value: c.id, label: `${c.name}${c.isBillable ? ' ($)' : ''}` })),
                ]}
                value={
                  config.defaultWorklogCategoryId
                    ? worklogCategories
                        .map((c) => ({ value: c.id, label: `${c.name}${c.isBillable ? ' ($)' : ''}` }))
                        .find((opt) => opt.value === config.defaultWorklogCategoryId) ?? { value: '', label: 'Use system default' }
                    : { value: '', label: 'Use system default' }
                }
                onChange={(option) => {
                  if (option) {
                    const selected = option as { value: string; label: string };
                    updateConfig('defaultWorklogCategoryId', selected.value || null);
                  }
                }}
                insideOverflowContainer
                className="rp-board-settings__dropdown"
              />
              <p className="rp-board-settings__hint">
                Pre-select this category when logging time on this board. Users can still change it per entry.
              </p>
            </div>
          )}
        </section>

      </div>

      {/* Footer with save/cancel */}
      <div className="rp-board-settings__footer">
        {error && (
          <p className="rp-board-settings__error-msg">{error}</p>
        )}
        <div className="rp-board-settings__footer-actions">
          <Button kind={Button.kinds.TERTIARY} onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BoardSettings;
